import { Schema } from 'effect';

import type { IntFilter, StringFilter } from '../../generated/types';

export const enumQueryOperators = <Filter>(wrap: (value: StringFilter) => Filter) => ({
  '=': (value: string) => wrap({ equalToInsensitive: value }),
  '!=': (value: string) => wrap({ notEqualToInsensitive: value }),
  IN: (values: readonly string[]) => wrap({ inInsensitive: [...values] }),
  'NOT IN': (values: readonly string[]) => wrap({ notInInsensitive: [...values] }),
  'IS EMPTY': () => wrap({ isNull: true }),
  'IS NOT EMPTY': () => wrap({ isNull: false }),
});

export const stringQueryOperators = <Filter>(wrap: (value: StringFilter) => Filter) => ({
  '~': (value: string) => wrap({ includesInsensitive: value }),
  ...enumQueryOperators(wrap),
  '!~': (value: string) => wrap({ notIncludesInsensitive: value }),
});

export const integerQueryOperators = <Filter>(wrap: (value: IntFilter) => Filter) => ({
  '=': (value: number) => wrap({ equalTo: value }),
  '!=': (value: number) => wrap({ notEqualTo: value }),
  '>': (value: number) => wrap({ greaterThan: value }),
  '>=': (value: number) => wrap({ greaterThanOrEqualTo: value }),
  '<': (value: number) => wrap({ lessThan: value }),
  '<=': (value: number) => wrap({ lessThanOrEqualTo: value }),
  IN: (values: readonly number[]) => wrap({ in: [...values] }),
  'NOT IN': (values: readonly number[]) => wrap({ notIn: [...values] }),
});

export const graphqlIntegerSchema = Schema.Int.check(
  Schema.isBetween(
    {
      minimum: -2_147_483_648,
      maximum: 2_147_483_647,
    },
    { message: 'Enter a whole number between -2147483648 and 2147483647' },
  ),
);
const positiveGraphqlIdSchema = graphqlIntegerSchema.check(Schema.isGreaterThan(0));
const userIdPattern = /^\d+$/;

export const isPositiveGraphqlId = (value: string): boolean =>
  userIdPattern.test(value.trim()) && Schema.is(positiveGraphqlIdSchema)(Number(value));

/**
 * A Discord user id is a snowflake: 17 to 20 decimal digits. Every one of them is past
 * the int32 range `isPositiveGraphqlId` accepts, so no input satisfies both, and a
 * pasted Discord id can be told apart from a TRM user id by shape alone. Compared as
 * a string: the values overflow a JS number.
 */
const discordUserIdPattern = /^\d{17,20}$/;

export const isDiscordUserId = (value: string): boolean => discordUserIdPattern.test(value.trim());
